import { supabase } from '@/integrations/supabase/client';
import { extractFunctionError } from '@/utils/errorUtils';
import type { Bundle } from '@financialharmony/show-and-tell';

// FIN-869: calls feedback-api's submitFeedback action via supabase.functions.invoke, so the
// call is correlated automatically through correlatedFetch (src/clients/correlatedFetch.ts).

export interface SubmitFeedbackInput {
  message: string;
  clientSubmissionId: string;
  app: 'consultant';
  route?: string;
  context: Record<string, string>;
  sessionId: string;
  recentCorrelationIds: readonly string[];
  sentryEventId?: string;
  sentryReplayId?: string;
}

export interface SubmitFeedbackResult {
  id: string;
  correlationId: string | null;
  isExisting: boolean;
}

/** Thrown on a non-2xx response; `code` lets `FeedbackPanel.tsx` branch without matching message text. */
export class FeedbackApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly reference?: string,
  ) {
    super(message);
    this.name = 'FeedbackApiError';
  }
}

export interface CreateRecordingUploadInput {
  clientSubmissionId: string;
  mimeType: string;
  bytes: number;
}

export interface CreateRecordingUploadResult {
  recordingId: string;
  uploadUrl: string | null;
  maxBytes: number;
}

export interface FinalizeRecordingInput {
  recordingId: string;
  clientSubmissionId: string;
  app: 'consultant';
  /** A typed note; empty when the user only talked. The server stores a placeholder then (EC21). */
  message: string;
  route?: string;
  context: Record<string, string>;
  sessionId: string;
  recentCorrelationIds: readonly string[];
  sentryEventId?: string;
  sentryReplayId?: string;
  bundle: Bundle;
}

export class FeedbackApiClient {
  async submitFeedback(input: SubmitFeedbackInput): Promise<SubmitFeedbackResult> {
    return this.invoke<SubmitFeedbackResult>({
      action: 'submitFeedback',
      message: input.message,
      clientSubmissionId: input.clientSubmissionId,
      app: input.app,
      route: input.route,
      context: input.context,
      sessionId: input.sessionId,
      recentCorrelationIds: [...input.recentCorrelationIds],
      sentryEventId: input.sentryEventId,
      sentryReplayId: input.sentryReplayId,
    });
  }

  /** FIN-1533: asks for a signed URL so the audio goes straight to storage, never through an edge function body. */
  async createRecordingUpload(input: CreateRecordingUploadInput): Promise<CreateRecordingUploadResult> {
    return this.invoke<CreateRecordingUploadResult>({
      action: 'createRecordingUpload',
      clientSubmissionId: input.clientSubmissionId,
      mimeType: input.mimeType,
      bytes: input.bytes,
    });
  }

  /** FIN-1533: saves one feedback row linked to the uploaded recording; idempotent on clientSubmissionId. */
  async finalizeRecording(input: FinalizeRecordingInput): Promise<SubmitFeedbackResult> {
    return this.invoke<SubmitFeedbackResult>({
      action: 'finalizeRecording',
      recordingId: input.recordingId,
      clientSubmissionId: input.clientSubmissionId,
      app: input.app,
      message: input.message,
      route: input.route,
      context: input.context,
      sessionId: input.sessionId,
      recentCorrelationIds: [...input.recentCorrelationIds],
      sentryEventId: input.sentryEventId,
      sentryReplayId: input.sentryReplayId,
      bundle: input.bundle,
    });
  }

  private async invoke<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase.functions.invoke('feedback-api', { body });

    if (error) {
      // FunctionsFetchError/FunctionsRelayError (network failure): no response ever arrived, so
      // there is no structured body or reference to read — matches handleError.ts's identical
      // distinction for the same two error names.
      const failure = await extractFunctionError(error);
      throw new FeedbackApiError(
        failure?.message ?? 'Something went wrong sending your feedback.',
        failure?.error ?? 'UNKNOWN',
        failure?.reference,
      );
    }

    return data as T;
  }
}
